import "share-api-polyfill";
import QRCode from "qrcode";

import type {
  ResponseBody,
  Impact,
  EmailOnlyUser,
  NamedUser,
} from "../../../functions/src/endpoints/certificate";
import type { DonateeGroup } from "../../../functions/src/data/donatee-group";

type Lang = "de" | "en";

function getLang(): Lang {
  const lang = document.querySelector("html")?.lang;
  return lang === "de" ? "de" : "en";
}

function formatNum(num: string, slice = 3): string {
  const len = num.length;
  return Array.from({ length: Math.ceil(len / slice) })
    .reduce(
      (acc: string[], _, i) => [
        num.substring(len - (i + 1) * slice, len - i * slice),
        ...acc,
      ],
      []
    )
    .join(" ");
}

function parseCertificateNum(location: Location): string | null {
  const { 1: num } = location.pathname.match(/\/certificate\/([0-9]+)$/) || [];
  const { 1: altNum } = location.hash.match(/^#([0-9]+)$/) || [];
  return num || altNum || null;
}

function provideShareCertificate(url: string) {
  const lang = getLang();
  const $share = document.getElementById("share") as HTMLButtonElement;
  if (!$share) return;

  $share.disabled = false;
  $share.addEventListener("click", () => {
    navigator.share(
      { url, title: "", text: "" },
      /* extra options for https://www.npmjs.com/package/share-api-polyfill */
      /* @ts-ignore */
      { skype: false, language: lang }
    );
  });
}

function displayCertificateNum(num: string) {
  const $num = document.getElementById("num");
  if (!$num) return null;

  $num.textContent = formatNum(num);
  removeClassOn($num);
}

const isEmailOnlyUser = (user: any): user is EmailOnlyUser =>
  user.slug === undefined || user.nickname === undefined;

const isNamedUser = (user: any): user is NamedUser =>
  user.slug !== undefined && user.nickname !== undefined;

function displayCertified(user: EmailOnlyUser | NamedUser) {
  const $certified = document.getElementById("certified") as HTMLAnchorElement;
  const certifiedHrefTemplate = $certified?.dataset.hrefTemplate;
  const certifiedHrefTemplateBiz = $certified?.dataset.hrefTemplateBiz;
  const $link = document.getElementById("certifiedLink") as HTMLAnchorElement;
  const { hrefTemplate, hrefTemplateBiz } = $link?.dataset;
  const $logo = document.getElementById("certifiedLogo") as HTMLImageElement;
  const srcTemplate = $logo?.dataset.srcTemplate;

  if (
    !$certified ||
    !certifiedHrefTemplate ||
    !certifiedHrefTemplateBiz ||
    !$link ||
    !hrefTemplate ||
    !hrefTemplateBiz ||
    !$logo ||
    !srcTemplate
  ) {
    throw Error("markup missing");
  }

  if (isNamedUser(user)) {
    const certifiedTemplate = user.isBusinessUser
      ? certifiedHrefTemplateBiz
      : certifiedHrefTemplate;
    $certified.href = certifiedTemplate.replace("{slug}", user.slug);
    $certified.textContent = user.nickname;
    const template = user.isBusinessUser ? hrefTemplateBiz : hrefTemplate;
    $link.href = template.replace("{slug}", user.slug);
  } else {
    $certified.classList.remove("underline");
    $certified.textContent = user.disguisedEmail;
  }
  removeClassOn($certified);
  $logo.src = srcTemplate.replace("{gravatarHash}", user.gravatarHash);
}

function displayPartner(partner: string) {
  const id = partner.replace(/\s+/g, "");
  const getBy = (name: string) => document.getElementById(`by${name}`);
  const getPartner = (name: string) =>
    document.getElementById(`partner${name}`);
  const $by = getBy(id);
  const $byPlaceholder = getBy("Placeholder");
  const $partner = getPartner(id);
  const $partnerPlaceholder = getPartner("Placeholder");
  if ($by) {
    $by.removeAttribute("hidden");
    $byPlaceholder?.setAttribute("hidden", "");
  } else if ($byPlaceholder) {
    removeClassOn($byPlaceholder);
    if (partner) {
      $byPlaceholder.textContent = partner;
    }
  }
  $partner?.removeAttribute("hidden");
  $partnerPlaceholder?.setAttribute("hidden", "");
}

function displayCause(cause: DonateeGroup) {
  const $cause = document.getElementById(`cause${cause}`);
  const $causePlaceholder = document.getElementById("causePlaceholder");
  $cause?.removeAttribute("hidden");
  $causePlaceholder?.setAttribute("hidden", "");
}

function displayImpact(impact: Impact) {
  const $impact = document.getElementById("impact");
  if (!$impact) return;
  const template = $impact?.getAttribute(
    `data-impact-${impact.cause}-template`
  );
  if (template) {
    const [templ1, templN] = template.split("|");
    const templ = templN && impact.value !== 1 ? templN : templ1;
    $impact.textContent = templ.replace(
      "{value}",
      impact.value.toLocaleString("en")
    );
  }
  removeClassOn($impact);
  displayCause(impact.cause);
  displayPartner(impact.partner);
}

function displayIssuedOn(timestamp: number) {
  const lang = getLang();
  const $issuedOn = document.getElementById("issuedOn");
  if (!$issuedOn) return;
  const date = new Date(timestamp);
  $issuedOn.textContent = date.toLocaleString(lang, { dateStyle: "long" });
  removeClassOn($issuedOn);
}

function displayCO2(impact: Impact) {
  // each tree binds 10 kg CO₂/year in the first 20 years
  const co2FactorTreesPlanted = 10;

  // 1sqm prevents 49 kg of bound CO₂ from entering the atmosphere
  const co2FactorSqmHabitatSaved = 49;

  // do nothing for CleanOcean
  if (impact.cause === "CleanOcean") return;

  const $co2Info = document.getElementById("co2Info");
  const $co2PlantTrees = document.getElementById("co2PlantTrees");
  const $co2PlantTreesValue = document.getElementById("co2PlantTreesValue");
  const $co2ProtectWildlife = document.getElementById("co2ProtectWildlife");
  const $co2ProtectWildlifeValue = document.getElementById(
    "co2ProtectWildlifeValue"
  );
  if (
    !$co2Info ||
    !$co2PlantTrees ||
    !$co2PlantTreesValue ||
    !$co2ProtectWildlife ||
    !$co2ProtectWildlifeValue
  ) {
    console.error("Some CO2 html elements missing");
    return;
  }

  switch (impact.cause) {
    case "PlantTrees":
      $co2PlantTreesValue.textContent = (
        impact.value * co2FactorTreesPlanted
      ).toLocaleString("en");
      $co2PlantTrees.removeAttribute("hidden");
      break;
    case "ProtectWildlife":
      $co2ProtectWildlifeValue.textContent = (
        impact.value * co2FactorSqmHabitatSaved
      ).toLocaleString("en");
      $co2ProtectWildlife.removeAttribute("hidden");
      break;
  }
  $co2Info.removeAttribute("hidden");
}

function provideQRCode(url: string) {
  const $canvas = document.getElementById("qrcode");
  if (!$canvas) return;

  QRCode.toCanvas(
    $canvas,
    url,
    { color: { dark: "#202C55" }, margin: 0 },
    function (error) {
      if (error) console.error(error);
    }
  );
  removeClassOn($canvas);
}

function removeClassOn($el: HTMLElement) {
  const removeClasses = $el.dataset.removeClass?.split(" ") || [];
  $el.classList.remove(...removeClasses);
}

async function init() {
  const num = parseCertificateNum(document.location);
  if (num) {
    const url = document.location.href;

    let data: ResponseBody;
    try {
      data = await fetch(`/api/certificate/${num}`).then((res) => res.json());
    } catch (err) {
      alert(`Sorry! Certificate no. ${num} was not found!`);
      document.location.href = "/";
      return;
    }
    /* XXX: test data
    const data: ResponseBody = {
      certified: {
        gravatarHash: "bf15391ebfd71c400ca934ff18288d0a",
        disguisedEmail: "mar...@...a.de",
        slug: "martin",
        nickname: "Martin",
      },
      num: "377085462941292960982030434876194023",
      impact: { cause: "CleanOcean", value: 4, partner: "Seven Clean Seas" },
      issuedAt: 1671735376619,
    };
    await new Promise((resolve) => {
      setTimeout(resolve, 1000);
    });
    // ----- */

    displayCertificateNum(data.num);
    displayCertified(data.certified);
    displayImpact(data.impact);
    displayCO2(data.impact);
    displayIssuedOn(data.issuedAt);
    provideShareCertificate(url);
    provideQRCode(url);
  } else {
    console.error("Cannot extract certificate number");
  }
}

init();
